import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowUpRight,
  Award,
  Check,
  Copy,
  Gauge,
  Handshake,
  ExternalLink,
  Loader2,
  Settings,
  Wallet,
  TrendingUp,
  Pencil,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import {
  getMyPartnerDashboard,
  listMyConversions,
  listMyPayouts,
  requestMyPayout,
  updateMyPartnerSettings,
  updateMyReferralCode,
} from "@/lib/referral/referral.functions";

export const Route = createFileRoute("/_account/account/referrals")({
  head: () => ({
    meta: [
      { title: "Partner referrals | FIXHEN" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AccountReferralsPage,
});

type Tab = "overview" | "commissions" | "payouts" | "settings";

const eur = (cents: number) =>
  `€${(Math.max(0, Math.round(Number(cents ?? 0))) / 100).toLocaleString("en-IE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const bpToPct = (bp: number) => `${(Number(bp ?? 0) / 100).toFixed(bp % 100 === 0 ? 0 : 2)}%`;

const statusChip: Record<string, { label: string; cls: string }> = {
  pending: { label: "Holding", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Available", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  paid: { label: "Paid out", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  reversed: { label: "Reversed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const payoutStatusChip: Record<string, { label: string; cls: string }> = {
  requested: { label: "Requested", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  processing: { label: "Processing", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "Failed", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const tierMeta: Record<string, { label: string; ring: string; grad: string }> = {
  silver: { label: "Silver", ring: "ring-slate-300", grad: "from-slate-500 to-slate-700" },
  gold: { label: "Gold", ring: "ring-amber-300", grad: "from-amber-400 to-amber-600" },
  platinum: { label: "Platinum", ring: "ring-violet-300", grad: "from-violet-500 to-fuchsia-600" },
};

function AccountReferralsPage() {
  const [tab, setTab] = useState<Tab>("overview");
  const { data, isLoading } = useQuery({
    queryKey: ["account", "referrals", "dashboard"],
    queryFn: () => getMyPartnerDashboard(),
  });

  if (isLoading) {
    return <div className="mt-6 text-sm text-slate-500">Loading…</div>;
  }

  if (!data?.partner) {
    return <NotAPartnerYet />;
  }

  const p = data.partner;
  const s = data.settings;
  const summary = data.summary;

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: "overview", label: "Overview", icon: Gauge },
    { id: "commissions", label: "Commissions", icon: TrendingUp },
    { id: "payouts", label: "Payouts", icon: Wallet },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Partner referrals</h1>
          <p className="mt-1 text-sm text-slate-500">
            Track clicks, commissions and payouts for your unique link.
          </p>
        </div>
        <PartnerStatusPill status={p.status} tier={p.tier} />
      </div>

      {p.status !== "approved" ? <ApplicationStatusBanner status={p.status} reason={p.rejected_reason ?? p.suspended_reason} /> : null}

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-1">
        <div className="flex flex-wrap gap-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition ${
                  active
                    ? "bg-[#172554] text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        {tab === "overview" ? (
          <OverviewTab partner={p} settings={s} summary={summary} daily={data.recentDaily} />
        ) : null}
        {tab === "commissions" ? <CommissionsTab /> : null}
        {tab === "payouts" ? <PayoutsTab partner={p} settings={s} summary={summary} /> : null}
        {tab === "settings" ? <SettingsTab partner={p} settings={s} /> : null}
      </div>
    </div>
  );
}

/* -------------------- Empty state (not a partner yet) -------------------- */

function NotAPartnerYet() {
  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Partner referrals</h1>
      <p className="mt-1 text-sm text-slate-500">Earn commission every time someone buys through your link.</p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50">
        <div className="p-8 sm:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#172554]/5 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#172554]">
            <Handshake className="h-3.5 w-3.5" /> Partner program
          </div>
          <h2 className="mt-3 text-2xl font-black text-[#172554]">
            Turn your audience into recurring income
          </h2>
          <p className="mt-2 max-w-xl text-sm text-slate-600">
            Get a unique link, earn <strong>up to 15%</strong> on every order, and climb the Silver → Gold → Platinum ladder as your volume grows. Payouts monthly, tracked for 90 days.
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/referral/apply"
              className="inline-flex items-center gap-2 rounded-xl bg-[#172554] px-5 py-3 text-sm font-bold text-white shadow-sm hover:bg-royal"
            >
              Apply to become a partner <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/referral"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Learn how it works
            </Link>
          </div>

          <ul className="mt-6 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
            <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-emerald-600" /> 90-day attribution window</li>
            <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-emerald-600" /> Monthly EUR payouts, low minimum</li>
            <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-emerald-600" /> Real-time dashboard &amp; QR code</li>
            <li className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-emerald-600" /> Free promo assets on approval</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Status + banners -------------------- */

function PartnerStatusPill({ status, tier }: { status: string; tier: string }) {
  const t = tierMeta[tier] ?? tierMeta.silver;
  if (status === "approved") {
    return (
      <div className={`inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${t.grad} px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-sm`}>
        <Award className="h-3.5 w-3.5" /> {t.label} partner
      </div>
    );
  }
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: "Application under review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    suspended: { label: "Account suspended", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    rejected: { label: "Application not accepted", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  };
  const c = map[status] ?? map.pending;
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider ${c.cls}`}>
      {c.label}
    </span>
  );
}

function ApplicationStatusBanner({ status, reason }: { status: string; reason?: string | null }) {
  if (status === "pending") {
    return (
      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        We're reviewing your application. You'll be able to share your link the moment it's approved (usually within 2 business days).
      </div>
    );
  }
  if (status === "suspended") {
    return (
      <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
        Your partner account is currently suspended{reason ? `: ${reason}` : ""}. Contact support to resolve this.
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
        Your application was not accepted{reason ? `: ${reason}` : ""}. Reach out if you'd like to reapply.
      </div>
    );
  }
  return null;
}

/* -------------------- Overview -------------------- */

/** Referral links must always point at the public domain — never at a
 *  preview/staging origin, otherwise partners share links that break. */
const SITE_URL = "https://fixhen.com";
function baseUrl() {
  return SITE_URL;
}

function OverviewTab({
  partner,
  settings,
  summary,
  daily,
}: {
  partner: any;
  settings: any;
  summary: any;
  daily: Array<{ day: string; conversions: number; commission_cents: number }>;
}) {
  const link = `${baseUrl()}/?ref=${partner.code}`;
  const rateBp =
    partner.manual_rate_override_bp ??
    (partner.tier === "platinum"
      ? settings?.tier_platinum_bp
      : partner.tier === "gold"
      ? settings?.tier_gold_bp
      : settings?.tier_silver_bp) ??
    1000;

  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };

  const nextTier = partner.tier === "silver" ? "gold" : partner.tier === "gold" ? "platinum" : null;
  const goldThreshold = Number(settings?.gold_threshold_cents ?? 100_000);
  const platThreshold = Number(settings?.platinum_threshold_cents ?? 1_000_000);
  const goalCents = partner.tier === "silver" ? goldThreshold : partner.tier === "gold" ? platThreshold : 0;
  const progress = goalCents > 0 ? Math.min(1, Number(summary.lifetime_subtotal_cents) / goalCents) : 1;

  const maxCommission = Math.max(1, ...daily.map((d) => d.commission_cents));
  const totalPending = summary.pending_cents;
  const totalApproved = summary.approved_cents;

  return (
    <div className="space-y-5">
      {/* Link + QR */}
      {partner.status === "approved" ? (
        <div className="grid gap-5 lg:grid-cols-[1fr_auto]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Your unique link</div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <code className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-mono font-semibold text-[#172554] break-all">
                {link}
              </code>
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-2 rounded-lg bg-[#172554] px-3.5 py-2 text-sm font-bold text-white hover:bg-royal"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#172554]"
              >
                Open <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <div>Referral code: <span className="font-bold text-slate-700">{partner.code}</span></div>
              <div>Cookie window: <span className="font-bold text-slate-700">{settings?.cookie_days ?? 90} days</span></div>
              <div>Your rate: <span className="font-bold text-slate-700">{bpToPct(rateBp)}</span></div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:w-56 flex flex-col items-center">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Share the QR</div>
            <div className="mt-3 rounded-xl bg-white p-2 ring-1 ring-slate-200">
              <QRCodeSVG value={link} size={144} bgColor="#ffffff" fgColor="#172554" level="M" />
            </div>
            <div className="mt-3 text-[11px] text-slate-500 text-center">Print on cards, add to livestreams, share on stories.</div>
          </div>
        </div>
      ) : null}

      {partner.status === "approved" ? (
        <>
          <ReferralCodeCard partner={partner} />
          <HowToUseCard code={partner.code} cookieDays={settings?.cookie_days ?? 90} rate={bpToPct(rateBp)} />
        </>
      ) : null}

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Available to withdraw" value={eur(totalApproved)} accent="emerald" />
        <Kpi label="Holding (in review)" value={eur(totalPending)} accent="amber" />
        <Kpi label="Paid out lifetime" value={eur(summary.paid_cents)} accent="sky" />
        <Kpi label="Total sales volume" value={eur(summary.lifetime_subtotal_cents)} accent="slate" />
      </div>

      {/* Tier ladder */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Tier progress</div>
            <div className="mt-1 text-lg font-bold text-slate-900">
              {tierMeta[partner.tier]?.label ?? "Silver"} — {bpToPct(rateBp)} per sale
            </div>
          </div>
          {nextTier ? (
            <div className="text-xs text-slate-500">
              Next: <span className="font-bold text-slate-700">{tierMeta[nextTier].label}</span> at{" "}
              <span className="font-bold text-slate-700">{eur(goalCents)}</span>
            </div>
          ) : (
            <div className="text-xs text-slate-500">You've reached the top tier — nice work.</div>
          )}
        </div>
        <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full bg-gradient-to-r ${tierMeta[partner.tier]?.grad ?? "from-slate-500 to-slate-700"}`}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-[11px] text-slate-500">
          <span>{eur(summary.lifetime_subtotal_cents)} lifetime volume</span>
          {nextTier ? <span>{eur(Math.max(0, goalCents - Number(summary.lifetime_subtotal_cents)))} to go</span> : <span>Max tier</span>}
        </div>
      </div>

      {/* 30-day sparkline */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Last 30 days</div>
            <div className="mt-1 text-lg font-bold text-slate-900">
              {daily.reduce((s, d) => s + d.conversions, 0)} conversions ·{" "}
              {eur(daily.reduce((s, d) => s + d.commission_cents, 0))} earned
            </div>
          </div>
        </div>
        <div className="mt-4 flex h-24 items-end gap-1">
          {daily.map((d) => (
            <div
              key={d.day}
              title={`${d.day}: ${d.conversions} orders · ${eur(d.commission_cents)}`}
              className="flex-1 rounded-t bg-gradient-to-t from-[#2563EB] to-[#7DD3FC]"
              style={{ height: `${Math.max(2, (d.commission_cents / maxCommission) * 100)}%`, opacity: d.commission_cents === 0 ? 0.15 : 1 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Referral code + instructions -------------------- */

function ReferralCodeCard({ partner }: { partner: any }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(partner.code ?? "");

  const m = useMutation({
    mutationFn: (code: string) => updateMyReferralCode({ data: { code } } as any),
    onSuccess: (res: any) => {
      toast.success(`Your code is now ${res.code}`);
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["account", "referrals"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Unable to update your code"),
  });

  const cooldownUntil = partner.code_changed_at
    ? new Date(new Date(partner.code_changed_at).getTime() + 7 * 86400_000)
    : null;
  const locked = !!cooldownUntil && cooldownUntil.getTime() > Date.now();
  const clean = value.trim().toUpperCase();
  const valid = /^[A-Z0-9][A-Z0-9-]{2,30}[A-Z0-9]$/.test(clean);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Your referral code</div>
          <p className="mt-1 text-sm text-slate-500">
            Make it memorable — it is the <code className="font-mono">?ref=</code> value in every link you share.
          </p>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={() => { setValue(partner.code ?? ""); setEditing(true); }}
            disabled={locked}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <Pencil className="h-3.5 w-3.5" /> Change code
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
            maxLength={32}
            spellCheck={false}
            className="rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm font-semibold uppercase tracking-wide text-[#172554] focus:border-[#2563EB] focus:outline-none"
            placeholder="FIXHEN-TFQS"
          />
          <button
            type="button"
            disabled={!valid || m.isPending}
            onClick={() => m.mutate(clean)}
            className="inline-flex items-center gap-2 rounded-lg bg-[#172554] px-4 py-2 text-sm font-bold text-white hover:bg-royal disabled:opacity-50"
          >
            {m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-sm font-semibold text-slate-500 hover:text-slate-800"
          >
            Cancel
          </button>
          <div className="w-full text-xs text-slate-500">
            4–32 characters, A–Z, 0–9 and dashes (not at the start or end). Old links stop working once you rename,
            and you can change it again after 7 days.
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <code className="rounded-lg bg-slate-100 px-3 py-2 font-mono text-lg font-black tracking-wide text-[#172554]">
            {partner.code}
          </code>
          {locked ? (
            <span className="text-xs text-slate-500">
              You can rename it again on {cooldownUntil!.toLocaleDateString()}.
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

function HowToUseCard({ code, cookieDays, rate }: { code: string; cookieDays: number; rate: string }) {
  const examples = [
    { label: "Homepage", url: `${SITE_URL}/?ref=${code}` },
    { label: "Whole shop", url: `${SITE_URL}/shop?ref=${code}` },
    { label: "A single product", url: `${SITE_URL}/p/your-product-slug?ref=${code}` },
    { label: "Blog article", url: `${SITE_URL}/blog/your-article?ref=${code}` },
  ];
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 text-[#2563EB]" />
        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">How to use your links</div>
      </div>
      <ol className="mt-3 space-y-2 text-sm text-slate-600 list-decimal pl-5">
        <li>
          Take <strong>any</strong> FIXHEN page URL and add <code className="font-mono">?ref={code}</code> at the end.
          If the address already contains a <code className="font-mono">?</code>, use{" "}
          <code className="font-mono">&amp;ref={code}</code> instead.
        </li>
        <li>
          We store the code in the visitor's browser for <strong>{cookieDays} days</strong>, so you still earn if they
          come back later and buy without your link.
        </li>
        <li>
          Every paid order counts <strong>{rate}</strong> of the product subtotal (shipping excluded) as your commission.
        </li>
        <li>Self-purchases with your own account are not counted, and refunds reverse the commission.</li>
      </ol>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {examples.map((e) => (
          <button
            key={e.label}
            type="button"
            onClick={() => copy(e.url)}
            className="group rounded-xl border border-slate-200 p-3 text-left hover:border-[#2563EB]/40 hover:bg-slate-50"
          >
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{e.label}</div>
            <div className="mt-1 break-all font-mono text-xs text-[#172554]">{e.url}</div>
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#2563EB] opacity-0 transition group-hover:opacity-100">
              <Copy className="h-3 w-3" /> Click to copy
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent: "emerald" | "amber" | "sky" | "slate" }) {
  const accents = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
    amber: "text-amber-700 bg-amber-50 border-amber-100",
    sky: "text-sky-700 bg-sky-50 border-sky-100",
    slate: "text-slate-700 bg-slate-50 border-slate-100",
  }[accent];
  return (
    <div className={`rounded-2xl border p-4 ${accents}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-black">{value}</div>
    </div>
  );
}

/* -------------------- Commissions -------------------- */

function CommissionsTab() {
  const [status, setStatus] = useState<"" | "pending" | "approved" | "paid" | "reversed">("");
  const { data, isLoading } = useQuery({
    queryKey: ["account", "referrals", "conversions", status],
    queryFn: () =>
      listMyConversions({ data: { limit: 50, offset: 0, ...(status ? { status } : {}) } as any }),
  });

  const rows = data?.rows ?? [];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(["", "pending", "approved", "paid", "reversed"] as const).map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setStatus(s as any)}
            className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wider transition ${
              status === s
                ? "border-[#172554] bg-[#172554] text-white"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
          >
            {s === "" ? "All" : statusChip[s].label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {isLoading ? (
          <div className="p-8 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <TrendingUp className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">No commissions yet — share your link to get the first one.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3 text-right">Sale</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-right">Commission</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r: any) => {
                const chip = statusChip[r.status] ?? statusChip.pending;
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">
                      #{r.order_number ?? r.order_id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">{eur(r.subtotal_cents)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{bpToPct(r.commission_rate_bp)}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">{eur(r.commission_cents)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${chip.cls}`}>
                        {chip.label}
                      </span>
                      {r.status === "pending" ? (
                        <div className="mt-0.5 text-[10px] text-slate-400">
                          Releases {new Date(r.hold_until).toLocaleDateString()}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* -------------------- Payouts -------------------- */

function PayoutsTab({ partner, settings, summary }: { partner: any; settings: any; summary: any }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["account", "referrals", "payouts"],
    queryFn: () => listMyPayouts(),
  });
  const rows = data?.rows ?? [];

  const minPayout = Number(settings?.min_payout_cents ?? 5000);
  const fee = Number(settings?.payout_fee_cents ?? 200);
  const available = Number(summary.approved_cents ?? 0);
  const canRequest = available >= minPayout && partner.status === "approved" && !!partner.payout_method;

  const mut = useMutation({
    mutationFn: () => requestMyPayout(),
    onSuccess: () => {
      toast.success("Payout requested");
      qc.invalidateQueries({ queryKey: ["account", "referrals"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Unable to request payout"),
  });

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Available for payout</div>
            <div className="mt-1 text-3xl font-black text-slate-900">{eur(available)}</div>
            <div className="mt-1 text-xs text-slate-500">
              Minimum {eur(minPayout)} · Fee {eur(fee)} · Paid via{" "}
              <span className="font-bold text-slate-700">{partner.payout_method ?? "—"}</span>
            </div>
          </div>
          <button
            type="button"
            disabled={!canRequest || mut.isPending}
            onClick={() => mut.mutate()}
            className="inline-flex items-center gap-2 rounded-xl bg-[#172554] px-5 py-3 text-sm font-bold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 hover:bg-royal"
          >
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
            Request payout
          </button>
        </div>
        {!canRequest && partner.status === "approved" ? (
          <p className="mt-3 text-xs text-slate-500">
            {!partner.payout_method
              ? "Add a payout method in Settings to enable withdrawals."
              : `You'll be able to request a payout once your available balance reaches ${eur(minPayout)}.`}
          </p>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {isLoading ? (
          <div className="p-8 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Wallet className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">No payouts yet.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Fee</th>
                <th className="px-4 py-3 text-right">Net</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r: any) => {
                const chip = payoutStatusChip[r.status] ?? payoutStatusChip.requested;
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-3 text-slate-500">{new Date(r.requested_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{r.method ?? "—"}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{eur(r.amount_cents)}</td>
                    <td className="px-4 py-3 text-right text-slate-500">−{eur(r.fee_cents)}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">{eur(r.net_cents)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${chip.cls}`}>
                        {chip.label}
                      </span>
                      {r.paid_at ? (
                        <div className="mt-0.5 text-[10px] text-slate-400">Paid {new Date(r.paid_at).toLocaleDateString()}</div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* -------------------- Settings -------------------- */

function SettingsTab({ partner, settings }: { partner: any; settings: any }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    display_name: partner.display_name ?? "",
    company: partner.company ?? "",
    country: partner.country ?? "",
    website: partner.website ?? "",
    payout_method: partner.payout_method ?? "bank",
    tax_id: partner.tax_id ?? "",
    payout_details_text: JSON.stringify(partner.payout_details ?? {}, null, 2),
  });

  const termsBehind =
    (partner.accepted_terms_version ?? 0) < (settings?.terms_version ?? 1);

  const mut = useMutation({
    mutationFn: async () => {
      let payout_details: Record<string, string> = {};
      try {
        const parsed = JSON.parse(form.payout_details_text || "{}");
        for (const [k, v] of Object.entries(parsed)) {
          if (v == null) continue;
          payout_details[k] = String(v);
        }
      } catch {
        throw new Error("Payout details must be valid JSON.");
      }
      return updateMyPartnerSettings({
        data: {
          display_name: form.display_name,
          company: form.company || null,
          country: (form.country || "").toUpperCase(),
          website: form.website || null,
          payout_method: form.payout_method as any,
          tax_id: form.tax_id || null,
          payout_details,
        } as any,
      });
    },
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["account", "referrals"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Unable to save"),
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-5">
      {termsBehind ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Our partner terms were updated. Please{" "}
          <Link to="/referral/terms" className="font-bold underline">
            review the new version
          </Link>{" "}
          and contact support to re-accept them.
        </div>
      ) : null}

      <form
        className="rounded-2xl border border-slate-200 bg-white p-5"
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Display name">
            <input className={inputCls} value={form.display_name} onChange={set("display_name")} required />
          </Field>
          <Field label="Company (optional)">
            <input className={inputCls} value={form.company} onChange={set("company")} />
          </Field>
          <Field label="Country (ISO-2)">
            <input className={inputCls} value={form.country} onChange={set("country")} maxLength={2} />
          </Field>
          <Field label="Website / channel URL">
            <input className={inputCls} value={form.website} onChange={set("website")} />
          </Field>
          <Field label="Payout method">
            <select className={inputCls} value={form.payout_method} onChange={set("payout_method")}>
              <option value="bank">Bank / SEPA</option>
              <option value="paypal">PayPal</option>
              <option value="wise">Wise</option>
              <option value="revolut">Revolut</option>
            </select>
          </Field>
          <Field label="Tax / VAT ID (optional)">
            <input className={inputCls} value={form.tax_id} onChange={set("tax_id")} />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="Payout details"
              hint='Sensitive — stored securely. Use JSON, e.g. {"iban":"…","bic":"…"} or {"email":"you@example.com"}.'
            >
              <textarea
                className={`${inputCls} font-mono text-xs`}
                rows={5}
                value={form.payout_details_text}
                onChange={set("payout_details_text")}
              />
            </Field>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Terms version accepted: <span className="font-bold text-slate-700">v{partner.accepted_terms_version ?? 0}</span>
          </div>
          <button
            type="submit"
            disabled={mut.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#172554] px-5 py-2.5 text-sm font-bold text-white hover:bg-royal disabled:opacity-60"
          >
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20";

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-[11px] text-slate-400">{hint}</span> : null}
    </label>
  );
}
